import { useState } from "react";
import SEO from "../../../components/SEO";
import img27 from "../../../assets/img27.webp";
import { CheckCircle, Plus, Minus } from "lucide-react";
import img10 from "../../../assets/img10.jpg";
import BlogSection from "../../../components/BlogSection";

export default function MortgageInsuranceHero() {
  const [activeIndex, setActiveIndex] = useState(null);
  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqs = [
    {
      question: "Qu'est-ce que l'assurance vie hypothécaire?",
      answer:
        "L'assurance vie hypothécaire est un type de protection qui permet de s'assurer que votre dette hypothécaire est remboursée si le propriétaire assuré décède. Contrairement à l'assurance vie traditionnelle qui verse les prestations directement aux bénéficiaires, l'assurance vie hypothécaire verse généralement le montant au prêteur pour réduire ou régler le solde hypothécaire impayé. Elle offre la tranquillité d'esprit que la maison de votre famille est protégée du stress financier, leur permettant de rester dans la maison sans se soucier des paiements hypothécaires.",
    },
    {
      question: "Comment fonctionne l'assurance vie hypothécaire au Canada?",
      answer:
        "L'assurance vie hypothécaire au Canada aide à protéger votre prêt immobilier en remboursant le solde hypothécaire restant si vous décédez avant que l'hypothèque soit entièrement payée. Vous choisissez un montant de couverture généralement égal à votre prêt impayé et payez des primes régulières. Si l'événement assuré survient, l'assureur verse la prestation pour réduire ou éliminer votre dette hypothécaire, aidant votre famille à rester dans la maison sans stress ni pression financière.",
    },
    {
      question: "L'assurance vie hypothécaire en vaut-elle la peine?",
      answer:
        "Oui, l'assurance vie hypothécaire est un moyen pratique de vous assurer que la maison de votre famille est protégée. Elle aide à couvrir le solde hypothécaire restant si quelque chose d'imprévu se produit, évitant le stress financier à vos proches. Cette couverture offre une sécurité, une stabilité et une tranquillité d'esprit à long terme, en faisant un investissement judicieux pour les propriétaires qui souhaitent protéger l'avenir de leur famille et conserver leur actif le plus important, leur maison.",
    },
    {
      question: "Devrais-je souscrire une assurance vie hypothécaire?",
      answer:
        "Oui, l'assurance vie hypothécaire est un choix judicieux pour les propriétaires qui souhaitent protéger leur famille et leur maison. Elle garantit que votre hypothèque peut être remboursée en cas d'imprévu, donnant à vos proches la possibilité de rester dans la maison sans stress financier. Ce type de couverture offre tranquillité d'esprit, sécurité et stabilité, en faisant un moyen abordable et pratique de protéger l'un de vos actifs les plus importants.",
    },
    {
      question: "Que couvre l'assurance vie hypothécaire?",
      answer:
        "L'assurance vie hypothécaire rembourse principalement le solde hypothécaire impayé si le propriétaire décède, protégeant votre famille de la perte de la maison. Certaines polices peuvent également aider à couvrir des frais connexes, comme les intérêts ou les frais de prêteur. En offrant ce soutien financier, elle permet à vos proches de maintenir leur mode de vie sans stress supplémentaire, garantissant la sécurité de la maison et du bien-être de la famille. Cette couverture offre un filet de sécurité fiable et pratique pour les propriétaires.",
    },
  ];

  return (
    <>
      <SEO
        title="Assurance hypothécaire Canada | Protégez votre prêt – einsured.ca"
        description="Protégez votre prêt immobilier au Canada avec einsured.ca et assurez la sécurité financière de votre famille grâce à une assurance hypothécaire fiable."
        keywords="Assurance hypothécaire Canada, assurance vie hypothécaire, protection prêt immobilier, courtier assurance hypothécaire"
      />

      {/* HERO SECTION */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start px-4 sm:px-6 md:px-20 text-center md:text-left"
        style={{ backgroundImage: `url(${img27})` }}
      >
        <div className="absolute inset-0 bg-black/40"></div>

        <div className="w-full max-w-[1380px] mx-auto">
          <div className="relative z-10 text-white max-w-xl">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4">
              Assurance hypothécaire
            </h1>
            <p className="text-base sm:text-lg md:text-xl font-semibold mb-8">
              Protégez votre maison, sécurisez vos rêves !
            </p>
            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Commencer avec l'assurance hypothécaire"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full shadow-md transition"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* INFO SECTION */}
      <section className="py-12 sm:py-16 md:py-16 bg-white px-4 sm:px-6 md:px-20">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-5xl mx-auto text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center">
              Assurance hypothécaire
            </h2>

            <p className="mt-6 sm:mt-8 text-base sm:text-lg leading-7 sm:leading-8">
              Protégez votre avenir avec{" "}
              <a
                href="/"
                className="text-sky-600 font-medium underline hover:opacity-90"
              >
                einsured.ca
              </a>{" "}
              – Renforcez votre sécurité financière grâce à l'assurance vie hypothécaire.
            </p>

            <p className="mt-6 text-base sm:text-lg leading-7 sm:leading-8">
              Félicitations pour cette étape importante dans votre vie : l'accession à la propriété !
            </p>

            <p className="mt-6 text-base sm:text-lg leading-7 sm:leading-8">
              Maintenant, assurons-nous que vous disposez de la{" "}
              <span className="text-[#20a7df] font-semibold">
                bonne assurance hypothécaire
              </span>{" "}
              pour protéger votre investissement. Bien que votre institution prêteuse puisse
              proposer des options d'assurance, celles-ci comportent souvent des limitations.
            </p>

            <p className="mt-6 text-base sm:text-lg leading-7 sm:leading-8">
              C'est là que nous intervenons.
            </p>

            <p className="mt-6 text-base sm:text-lg leading-7 sm:leading-8">
              Chez{" "}
              <a
                href="/"
                className="text-sky-600 font-medium underline hover:opacity-90"
              >
                einsured.ca
              </a>
              , nous sommes spécialisés dans la conception de solutions d'assurance hypothécaire
              sur mesure qui vous offrent plus de contrôle et une meilleure couverture.
            </p>
          </div>
        </div>
      </section>

      {/* --- WHAT TO CONSIDER --- */}
      <section className="py-12 sm:py-16 md:py-10 px-4 sm:px-6 md:px-20">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-5xl mx-auto text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6 sm:mb-10">
              Quoi considérer lors du choix d'une{" "}
              <span className="underline">assurance vie hypothécaire</span>
            </h2>
            <ul className="space-y-6 sm:space-y-8 text-base sm:text-lg leading-relaxed">
              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Continuité de la couverture :
                  </span>{" "}
                  Contrairement à l'assurance hypothécaire traditionnelle, il est important de
                  choisir une police qui vous accompagne, même si vous changez de prêteur lors
                  du renouvellement de votre hypothèque. Cela garantit une protection ininterrompue
                  tout au long de la durée de votre prêt.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Contrats personnalisés :
                  </span>{" "}
                  Recherchez des plans qui vous permettent de prendre les décisions qui vous
                  importent. Avec notre assurance, vous êtes propriétaire du contrat et avez
                  la liberté de choisir vos bénéficiaires.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Primes stables :
                  </span>{" "}
                  Choisissez une prime établie en fonction de votre profil unique et de votre
                  état de santé au moment de la souscription. Cela vous assure une tranquillité
                  d'esprit et une stabilité financière à long terme.
                </p>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* --- DID YOU KNOW --- */}
      <section className="py-16 sm:py-14 px-4 sm:px-6 md:px-20 bg-white">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-5xl mx-auto text-center">
            <div className="bg-[#f2f6fa] rounded-3xl shadow-md p-6 sm:p-8 md:p-12 text-left border border-gray-200">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#49628b] mb-4 sm:mb-6 text-center">
                Le saviez-vous ?
              </h3>
              <p className="text-base sm:text-lg leading-relaxed text-gray-800 mb-4 sm:mb-5">
                Souscrire une{" "}
                <a
                  href="https://www.canada.ca/en/financial-consumer-agency/services/mortgages/optional-insurance-products.html#toc1"
                  className="text-sky-600 underline hover:opacity-90"
                >
                  assurance vie hypothécaire
                </a>{" "}
                auprès d'une compagnie d'assurance est plus économique que de l'obtenir auprès
                d'une banque. En choisissant le bon plan, vous pouvez économiser sur les primes
                d'assurance tout en bénéficiant d'une couverture complète.
              </p>
              <p className="text-base sm:text-lg leading-relaxed text-gray-800">
                En cas d'événement malheureux, votre assurance couvre non seulement vos paiements
                hypothécaires, mais s'étend également aux autres prêts que vous pourriez avoir.
                Cela garantit que tous vos engagements financiers sont pris en charge dans les
                moments difficiles.
              </p>
            </div>

            {/* CTA Button */}
            <div className="mt-8 sm:mt-10">
              <a
                href="https://compulife.org/website/655/quoter.html"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold text-base sm:text-lg px-8 sm:px-10 py-3.5 sm:py-4 rounded-full shadow-md transition"
              >
                Obtenez votre meilleur taux
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* --- BANK VS ADVISOR COMPARISON --- */}
      <section className="py-12 sm:py-16 md:py-10 bg-white px-4 sm:px-6 md:px-20">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6 md:mb-8">
              Acheter une assurance hypothécaire auprès d'une banque ou d'un prêteur vs d'un conseiller en assurance
            </h2>

            <div className="md:hidden space-y-4">
              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="font-semibold text-[#49628b] mb-2">
                  Propriété de la police
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Assurance hypothécaire bancaire
                    </div>
                    <div className="text-gray-800">La banque est propriétaire de la police</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Achetée via un conseiller
                    </div>
                    <div className="text-gray-800">
                      L'emprunteur est propriétaire de la police
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="font-semibold text-[#49628b] mb-2">
                  Continuité de la couverture
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Assurance hypothécaire bancaire
                    </div>
                    <div className="text-gray-800">
                      Prend fin si l'hypothèque est transférée ou refinancée
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Achetée via un conseiller
                    </div>
                    <div className="text-gray-800">
                      La couverture demeure si l'hypothèque est transférée ou refinancée
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="font-semibold text-[#49628b] mb-2">
                  Bénéficiaire
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Assurance hypothécaire bancaire
                    </div>
                    <div className="text-gray-800">
                      La banque est le bénéficiaire, c.-à-d. le versement va directement à la banque
                      pour couvrir le solde hypothécaire restant
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Achetée via un conseiller
                    </div>
                    <div className="text-gray-800">
                      Le(s) bénéficiaire(s) choisi(s) par l'emprunteur, c.-à-d. le versement va
                      au bénéficiaire désigné, qui décide ensuite comment utiliser les fonds
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="font-semibold text-[#49628b] mb-2">
                  Personnalisation de la police
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Assurance hypothécaire bancaire
                    </div>
                    <div className="text-gray-800">
                      Personnalisation limitée ou inexistante
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Achetée via un conseiller
                    </div>
                    <div className="text-gray-800">
                      Hautement personnalisable selon les besoins de l'emprunteur
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="font-semibold text-[#49628b] mb-2">Coût</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Assurance hypothécaire bancaire
                    </div>
                    <div className="text-gray-800">
                      Les primes peuvent être inférieures à celles d'une assurance privée
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Achetée via un conseiller
                    </div>
                    <div className="text-gray-800">
                      Primes potentiellement plus compétitives
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="font-semibold text-[#49628b] mb-2">
                  Flexibilité
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Assurance hypothécaire bancaire
                    </div>
                    <div className="text-gray-800">Flexibilité limitée</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Achetée via un conseiller
                    </div>
                    <div className="text-gray-800">
                      Plus grande flexibilité dans les modalités et conditions de la police,
                      car l'assuré est propriétaire avec plein contrôle sur sa police
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="font-semibold text-[#49628b] mb-2">
                  Transparence
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Assurance hypothécaire bancaire
                    </div>
                    <div className="text-gray-800">
                      Moins de transparence, les polices sont intégrées à l'hypothèque
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Achetée via un conseiller
                    </div>
                    <div className="text-gray-800">
                      Plus transparente, les polices sont clairement définies
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="font-semibold text-[#49628b] mb-2">
                  Portabilité de l'assurance
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Assurance hypothécaire bancaire
                    </div>
                    <div className="text-gray-800">
                      Non portable, l'assurance hypothécaire bancaire est liée à votre prêteur,
                      ce qui signifie que vous perdez la couverture si vous changez de banque et
                      que le versement va directement à la banque, pas à votre famille
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Achetée via un conseiller
                    </div>
                    <div className="text-gray-800">
                      Avec einsured.ca, votre assurance hypothécaire est entièrement portable,
                      vous accompagne peu importe où est détenue votre hypothèque, et verse le
                      montant à votre bénéficiaire désigné — pas à la banque
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="font-semibold text-[#49628b] mb-2">
                  Souscription médicale
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Assurance hypothécaire bancaire
                    </div>
                    <div className="text-gray-800">
                      L'assurance hypothécaire bancaire omet généralement la souscription médicale
                      à l'avance, ce qui signifie que les réclamations peuvent être refusées ultérieurement
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500">
                      Achetée via un conseiller
                    </div>
                    <div className="text-gray-800">
                      Les polices d'einsured.ca sont entièrement souscrites médicalement au moment
                      de la demande, donc il n'y a aucune surprise au moment de la réclamation
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop Table */}
            <div className="overflow-x-auto rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.08)] hidden md:block">
              <table className="w-full border border-gray-300 text-gray-800 rounded-2xl">
                <caption className="sr-only">
                  Comparaison entre l'assurance hypothécaire bancaire et les options d'un conseiller en assurance
                </caption>
                <thead>
                  <tr className="bg-sky-400 text-white border-b border-gray-300 divide-x divide-white/30">
                    <th
                      scope="col"
                      className="text-left p-5 md:p-6 text-xl font-bold rounded-tl-2xl"
                    >
                      Aspect
                    </th>
                    <th
                      scope="col"
                      className="text-left p-5 md:p-6 text-xl font-bold"
                    >
                      Assurance hypothécaire bancaire
                    </th>
                    <th
                      scope="col"
                      className="text-left p-5 md:p-6 text-xl font-bold rounded-tr-2xl"
                    >
                      Achetée via un conseiller
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-300">
                  <tr className="divide-x divide-gray-300">
                    <td className="p-5 md:p-6 font-semibold text-[#49628b]">
                      Propriété de la police
                    </td>
                    <td className="p-5 md:p-6">La banque est propriétaire de la police</td>
                    <td className="p-5 md:p-6">L'emprunteur est propriétaire de la police</td>
                  </tr>

                  <tr className="divide-x divide-gray-300">
                    <td className="p-5 md:p-6 font-semibold text-[#49628b]">
                      Continuité de la couverture
                    </td>
                    <td className="p-5 md:p-6">
                      Prend fin si l'hypothèque est transférée ou refinancée
                    </td>
                    <td className="p-5 md:p-6">
                      La couverture demeure si l'hypothèque est transférée ou refinancée
                    </td>
                  </tr>

                  <tr className="divide-x divide-gray-300">
                    <td className="p-5 md:p-6 font-semibold text-[#49628b]">
                      Bénéficiaire
                    </td>
                    <td className="p-5 md:p-6">
                      La banque est le bénéficiaire, c.-à-d. le versement va directement à la banque
                      pour couvrir le solde hypothécaire restant
                    </td>
                    <td className="p-5 md:p-6">
                      Le(s) bénéficiaire(s) choisi(s) par l'emprunteur, c.-à-d. le versement va
                      au bénéficiaire désigné, qui décide ensuite comment utiliser les fonds
                    </td>
                  </tr>

                  <tr className="divide-x divide-gray-300">
                    <td className="p-5 md:p-6 font-semibold text-[#49628b]">
                      Personnalisation de la police
                    </td>
                    <td className="p-5 md:p-6">Personnalisation limitée ou inexistante</td>
                    <td className="p-5 md:p-6">
                      Hautement personnalisable selon les besoins de l'emprunteur
                    </td>
                  </tr>

                  <tr className="divide-x divide-gray-300">
                    <td className="p-5 md:p-6 font-semibold text-[#49628b]">
                      Coût
                    </td>
                    <td className="p-5 md:p-6">
                      Les primes peuvent être inférieures à celles d'une assurance privée
                    </td>
                    <td className="p-5 md:p-6">
                      Primes potentiellement plus compétitives
                    </td>
                  </tr>

                  <tr className="divide-x divide-gray-300">
                    <td className="p-5 md:p-6 font-semibold text-[#49628b]">
                      Flexibilité
                    </td>
                    <td className="p-5 md:p-6">Flexibilité limitée</td>
                    <td className="p-5 md:p-6">
                      Plus grande flexibilité dans les modalités et conditions de la police,
                      car l'assuré est propriétaire avec plein contrôle sur sa police
                    </td>
                  </tr>

                  <tr className="divide-x divide-gray-300">
                    <td className="p-5 md:p-6 font-semibold text-[#49628b]">
                      Transparence
                    </td>
                    <td className="p-5 md:p-6">
                      Moins de transparence, les polices sont intégrées à l'hypothèque
                    </td>
                    <td className="p-5 md:p-6">
                      Plus transparente, les polices sont clairement définies
                    </td>
                  </tr>

                  <tr className="divide-x divide-gray-300">
                    <td className="p-5 md:p-6 font-semibold text-[#49628b]">
                      Portabilité de l'assurance
                    </td>
                    <td className="p-5 md:p-6">
                      Non portable, l'assurance hypothécaire bancaire est liée à votre prêteur,
                      ce qui signifie que vous perdez la couverture si vous changez de banque et
                      que le versement va directement à la banque — pas à votre famille
                    </td>
                    <td className="p-5 md:p-6">
                      Avec einsured.ca, votre assurance hypothécaire est entièrement portable,
                      vous accompagne peu importe où est détenue votre hypothèque, et verse le
                      montant à votre bénéficiaire désigné — pas à la banque
                    </td>
                  </tr>

                  <tr className="divide-x divide-gray-300">
                    <td className="p-5 md:p-6 font-semibold text-[#49628b]">
                      Souscription médicale
                    </td>
                    <td className="p-5 md:p-6">
                      L'assurance hypothécaire bancaire omet généralement la souscription médicale
                      à l'avance, ce qui signifie que les réclamations peuvent être refusées ultérieurement
                    </td>
                    <td className="p-5 md:p-6">
                      Les polices d'einsured.ca sont entièrement souscrites médicalement au moment
                      de la demande, donc il n'y a aucune surprise au moment de la réclamation
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* --- TYPES OF MORTGAGE INSURANCE --- */}
      <section className="py-16 sm:py-14 bg-white px-4 sm:px-6 md:px-20">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-6xl mx-auto text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-4 sm:mb-6">
              Quelles sont les options disponibles en assurance hypothécaire ?
            </h2>

            <p className="text-center text-base sm:text-lg max-w-3xl mx-auto mb-8 sm:mb-10">
              Lorsqu'on considère l'assurance hypothécaire comme forme d'assurance vie,
              les particuliers disposent généralement de plusieurs options. Chaque option
              présente ses propres caractéristiques, avantages et considérations.
            </p>

            <h3 className="text-center text-xl sm:text-2xl font-semibold text-[#49628b] mb-10 sm:mb-14">
              Voici les principaux types d'options d'assurance hypothécaire :
            </h3>
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
              {/* Traditional Term Life Insurance */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl border border-gray-200 shadow-sm">
                <h4 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3 sm:mb-4">
                  Assurance vie temporaire traditionnelle
                </h4>
                <ul className="space-y-4 sm:space-y-5 text-base sm:text-lg leading-relaxed">
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    <p>
                      Avec l'{" "}
                      <a
                        href="/fr/assurance-vie-temporaire"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-600 underline hover:opacity-90"
                      >
                        assurance vie temporaire traditionnelle
                      </a>
                      , le titulaire de la police choisit une durée de couverture (p. ex. 10, 20
                      ou 30 ans) pendant laquelle la prestation de décès est en vigueur. Si le
                      titulaire décède pendant la période choisie, les bénéficiaires reçoivent
                      la prestation de décès. Ce type d'assurance peut servir à couvrir le solde
                      hypothécaire et d'autres besoins financiers.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    <p>
                      On parle également d'assurance temporaire à niveau — cette option garantit
                      que les bénéficiaires reçoivent un versement constant, indépendamment des
                      variations du solde hypothécaire. Elle peut convenir aux personnes souhaitant
                      une prestation de décès fixe pour une protection financière plus complète.
                    </p>
                  </li>
                </ul>
              </div>

              {/* Decreasing Term Life Insurance */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl border border-gray-200 shadow-sm">
                <h4 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3 sm:mb-4">
                  Assurance vie temporaire décroissante
                </h4>
                <ul className="space-y-4 sm:space-y-5 text-base sm:text-lg leading-relaxed">
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    <p>
                      L'assurance vie temporaire décroissante est spécifiquement conçue pour la
                      protection hypothécaire. La prestation de décès diminue au fil du temps,
                      s'alignant généralement sur le solde décroissant de l'hypothèque.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    <p>
                      Cela signifie que le versement couvrira le solde hypothécaire restant si
                      l'assuré décède. C'est une option économique pour ceux qui se préoccupent
                      principalement de protéger leur hypothèque.
                    </p>
                  </li>
                </ul>
              </div>

              {/* Permanent Life Insurance */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl border border-gray-200 shadow-sm">
                <h4 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3 sm:mb-4">
                  Assurance vie permanente
                </h4>
                <ul className="space-y-4 sm:space-y-5 text-base sm:text-lg leading-relaxed">
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    <p>
                      L'assurance vie permanente, comme l'{" "}
                      <a
                        href="/fr/assurance-vie-entiere"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-600 underline hover:opacity-90"
                      >
                        assurance vie entière
                      </a>{" "}
                      ou l'assurance vie universelle, offre une couverture pour toute la vie de
                      l'assuré. Ces polices comprennent également une composante de valeur de
                      rachat qui croît au fil du temps.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    <p>
                      Bien que généralement plus coûteuse que l'assurance vie temporaire,
                      l'assurance vie permanente offre une protection à vie et une accumulation
                      potentielle de valeur de rachat.
                    </p>
                  </li>
                </ul>
              </div>

              {/* Joint Insurance */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl border border-gray-200 shadow-sm">
                <h4 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3 sm:mb-4">
                  Assurance conjointe
                </h4>
                <ul className="space-y-4 sm:space-y-5 text-base sm:text-lg leading-relaxed">
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    <p>
                      L'assurance hypothécaire conjointe couvre les deux partenaires d'une
                      hypothèque avec une seule police. Si l'un des partenaires décède, la
                      prestation de décès peut servir à rembourser l'hypothèque. C'est une
                      option pratique pour les couples souhaitant une couverture commune.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    <p>
                      Lors du choix d'une assurance hypothécaire comme option d'assurance vie,
                      il est essentiel d'examiner attentivement les modalités de la police, les
                      montants de couverture, les coûts des primes et tout avenant ou
                      caractéristique supplémentaire.
                    </p>
                  </li>
                </ul>
              </div>

              {/* Critical Illness Insurance */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl border border-gray-200 shadow-sm">
                <h4 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3 sm:mb-4">
                  Assurance maladies graves (AMG)
                </h4>
                <ul className="space-y-4 sm:space-y-5 text-base sm:text-lg leading-relaxed">
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    <p>
                      Une maladie grave comme le cancer, une crise cardiaque ou un AVC peut
                      survenir sans avertissement — vous laissant incapable de travailler et
                      ayant du mal à payer votre hypothèque. L'assurance maladies graves
                      d'einsured.ca verse une somme forfaitaire non imposable au diagnostic
                      d'une maladie couverte, vous offrant un soulagement financier immédiat
                      lorsque vous en avez le plus besoin.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    <p>
                      Vous pouvez combiner la couverture AMG avec votre assurance vie et
                      invalidité, et la personnaliser selon votre terme hypothécaire ou votre
                      profil de risque. Le versement vous appartient et peut être utilisé comme
                      vous le souhaitez — que ce soit pour couvrir votre hypothèque, payer un
                      traitement ou prendre du temps pour vous rétablir — afin que vous puissiez
                      vous concentrer sur votre santé sans la pression financière.
                    </p>
                  </li>
                </ul>
              </div>

              {/* Disability Insurance */}
              <div className="bg-[#f2f6fa] p-6 sm:p-8 rounded-3xl border border-gray-200 shadow-sm">
                <h4 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3 sm:mb-4">
                  Assurance invalidité (AI)
                </h4>
                <ul className="space-y-4 sm:space-y-5 text-base sm:text-lg leading-relaxed">
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    <p>
                      Si vous êtes incapable de travailler en raison d'une maladie ou d'une
                      blessure, l'assurance invalidité d'einsured.ca aide à couvrir vos
                      paiements hypothécaires mensuels afin que vous puissiez rester sur la
                      bonne voie financièrement pendant votre rétablissement. Elle garantit
                      que votre maison reste sécurisée, en vous offrant un soutien constant
                      pendant une période où vos revenus peuvent être temporairement interrompus.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    <p>
                      Vous avez la flexibilité de personnaliser vos périodes de prestations et
                      d'attente selon vos besoins financiers. Pour plus d'accessibilité, vous
                      pouvez choisir une option de couverture décroissante qui réduit vos primes
                      au fil du temps à mesure que votre solde hypothécaire diminue — ce qui en
                      fait une solution pratique et adaptable.
                    </p>
                  </li>
                </ul>
              </div>
            </div>

            {/* CTA Button */}
            <div className="text-center mt-12 sm:mt-16">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold text-base sm:text-lg px-8 sm:px-10 py-3.5 sm:py-4 rounded-full shadow-md transition"
              >
                Réservez une consultation gratuite
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* --- SIGNIFICANCE OF MORTGAGE INSURANCE --- */}
      <section
        className="relative py-20 sm:py-24 md:py-20 px-4 sm:px-6 md:px-24 bg-cover bg-center bg-fixed"
        style={{ backgroundImage: `url(${img10})` }}
      >
        <div className="absolute inset-0 bg-sky-900 bg-opacity-75"></div>

        <div className="relative z-10 max-w-[1380px] mx-auto">
          <div className="max-w-6xl mx-auto text-center text-white">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 text-white">
              Importance de l'assurance hypothécaire
            </h2>

            <p className="max-w-4xl mx-auto text-base sm:text-lg leading-relaxed mb-8 sm:mb-10 text-gray-100 px-1">
              L'assurance hypothécaire, également connue sous le nom d'{" "}
              <span className="text-[#20a7df] font-semibold">
                assurance protection hypothécaire
              </span>
              , est conçue pour assurer la sécurité financière des propriétaires et de leur
              famille en cas de circonstances imprévues.
            </p>

            <h3 className="text-xl sm:text-2xl font-semibold mb-8 sm:mb-12 text-[#20a7df]">
              Voici plusieurs raisons pour lesquelles les particuliers peuvent trouver nécessaire
              ou avantageux de souscrire une assurance hypothécaire :
            </h3>

            {/* Card Grid */}
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-10">
              {/* Card 1 */}
              <div className="bg-white/95 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition text-gray-800">
                <h4 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#49628b]">
                  Protection financière pour vos proches :
                </h4>
                <p className="text-sm sm:text-base leading-relaxed text-gray-700">
                  L'une des principales raisons de considérer l'{" "}
                  <span className="font-semibold text-[#49628b]">
                    assurance hypothécaire
                  </span>{" "}
                  est de s'assurer que vos proches sont protégés financièrement en cas de
                  décès du titulaire de la police. L'assurance rembourse le solde hypothécaire
                  restant, évitant ainsi aux membres de la famille survivants de hériter du
                  fardeau de la dette hypothécaire.
                </p>
              </div>

              {/* Card 2 */}
              <div className="bg-white/95 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition text-gray-800">
                <h4 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#49628b]">
                  Sécuriser la résidence familiale :
                </h4>
                <p className="text-sm sm:text-base leading-relaxed text-gray-700">
                  Pour de nombreuses familles, la maison est un actif important et un lieu de
                  valeur sentimentale. L'assurance hypothécaire contribue à sécuriser la
                  résidence familiale, permettant aux membres survivants de continuer à y
                  vivre sans la menace de saisie due à une hypothèque impayée.
                </p>
              </div>

              {/* Card 3 */}
              <div className="bg-white/95 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition text-gray-800">
                <h4 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#49628b]">
                  Garantir le remboursement du prêt :
                </h4>
                <p className="text-sm sm:text-base leading-relaxed text-gray-700">
                  L'assurance hypothécaire garantit que le solde hypothécaire en cours est
                  remboursé, assurant ainsi au prêteur de recevoir le montant total dû. Cela
                  peut être particulièrement crucial dans les situations où le principal
                  soutien financier décède et que la famille peut avoir du mal à effectuer
                  les paiements hypothécaires.
                </p>
              </div>

              {/* Card 4 */}
              <div className="bg-white/95 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition text-gray-800">
                <h4 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#49628b]">
                  Tranquillité d'esprit :
                </h4>
                <p className="text-sm sm:text-base leading-relaxed text-gray-700">
                  L'assurance hypothécaire offre une tranquillité d'esprit aux propriétaires,
                  sachant que leurs proches seront protégés du fardeau financier de la dette
                  hypothécaire. Cette tranquillité d'esprit permet aux individus de se
                  concentrer sur leur vie quotidienne sans s'inquiéter constamment de ce qui
                  se passerait s'ils n'étaient plus là pour subvenir aux besoins de leur famille.
                </p>
              </div>

              {/* Card 5 */}
              <div className="bg-white/95 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition text-gray-800">
                <h4 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#49628b]">
                  Couverture en cas d'invalidité ou de maladie grave :
                </h4>
                <p className="text-sm sm:text-base leading-relaxed text-gray-700">
                  Certaines polices d'assurance hypothécaire offrent une couverture
                  supplémentaire en cas d'{" "}
                  <a
                    href="/fr/assurance-invalidite"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600 underline"
                  >
                    invalidité
                  </a>{" "}
                  ou de{" "}
                  <a
                    href="/fr/assurance-maladies-graves"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600 underline"
                  >
                    maladie grave
                  </a>
                  . Cela peut fournir une aide financière au titulaire de la police s'il est
                  incapable de travailler et aider à couvrir les paiements hypothécaires
                  pendant une période difficile.
                </p>
              </div>

              {/* Card 6 */}
              <div className="bg-white/95 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition text-gray-800">
                <h4 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#49628b]">
                  Flexibilité des options de police :
                </h4>
                <p className="text-sm sm:text-base leading-relaxed text-gray-700">
                  Les polices d'assurance hypothécaire offrent souvent diverses options,
                  permettant aux individus d'adapter la couverture à leurs besoins spécifiques.
                  Cette flexibilité peut inclure le choix des montants de couverture, des
                  durées de police et des avenants supplémentaires pour l'{" "}
                  <a
                    href="/fr/assurance-invalidite"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600 underline"
                  >
                    invalidité
                  </a>{" "}
                  ou les{" "}
                  <a
                    href="/fr/assurance-maladies-graves"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600 underline"
                  >
                    maladies graves
                  </a>
                  .
                </p>
              </div>

              {/* Card 7 */}
              <div className="bg-white/95 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition text-gray-800">
                <h4 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#49628b]">
                  Accessibilité financière :
                </h4>
                <p className="text-sm sm:text-base leading-relaxed text-gray-700">
                  L'assurance hypothécaire est souvent plus abordable que les polices
                  d'assurance vie traditionnelles. Les primes sont généralement moins élevées,
                  ce qui en fait une option accessible pour les personnes souhaitant s'assurer
                  que leur hypothèque est couverte sans fardeau financier important.
                </p>
              </div>

              {/* Card 8 */}
              <div className="bg-white/95 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition text-gray-800">
                <h4 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#49628b]">
                  Processus de demande simplifié :
                </h4>
                <p className="text-sm sm:text-base leading-relaxed text-gray-700">
                  Le processus de demande d'assurance hypothécaire est généralement simple,
                  avec moins d'exigences de souscription que les polices d'assurance vie
                  traditionnelles. Cette facilité de demande chez{" "}
                  <a
                    href="/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600 underline"
                  >
                    einsured.ca
                  </a>{" "}
                  en fait une option pratique pour les propriétaires.
                </p>
              </div>

              {/* Card 9 */}
              <div className="bg-white/95 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition text-gray-800 md:col-span-2">
                <h4 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#49628b]">
                  Versement rapide :
                </h4>
                <p className="text-sm sm:text-base leading-relaxed text-gray-700">
                  En cas d'incident couvert, les polices d'{" "}
                  <a href="#" className="text-sky-600 underline">
                    assurance hypothécaire
                  </a>{" "}
                  fournissent généralement un versement rapide aux bénéficiaires. Cela peut
                  être crucial pour s'assurer que les fonds sont disponibles rapidement pour
                  régler l'hypothèque en cours et réduire le stress financier.
                </p>
              </div>
            </div>

            <p className="text-gray-100 text-base sm:text-lg max-w-4xl mx-auto leading-relaxed mb-8 sm:mb-10 px-1">
              Bien que l'assurance hypothécaire offre des avantages précieux, les individus
              devraient examiner attentivement les modalités de la police, les limitations
              de couverture et les options alternatives pour déterminer si elle correspond
              à leurs besoins et à leur situation particuliers.
            </p>

            {/* CTA Button */}
            <div className="text-center mt-6 sm:mt-8">
              <a
                href="https://compulife.org/website/655/quoter.html"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold text-base sm:text-lg px-8 sm:px-10 py-3.5 sm:py-4 rounded-full shadow-md transition"
              >
                Obtenez votre meilleur taux
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Frequently Asked Questions */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#49628b]">
              Foire aux questions sur l'assurance hypothécaire
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => {
              const expanded = activeIndex === index;
              const panelId = `faq-panel-${index}`;
              const btnId = `faq-btn-${index}`;

              return (
                <div
                  key={index}
                  className="border rounded-lg shadow-sm transition-all bg-white"
                >
                  <button
                    id={btnId}
                    className="w-full flex justify-between items-center text-left px-4 sm:px-6 py-3.5 sm:py-4 font-semibold text-base sm:text-lg text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1e48a0] focus-visible:ring-offset-2"
                    onClick={() => toggleFAQ(index)}
                    aria-expanded={expanded}
                    aria-controls={panelId}
                  >
                    <span>{faq.question}</span>

                    {expanded ? (
                      <Minus
                        className="w-6 h-6 text-gray-700 shrink-0 ml-4"
                        aria-hidden="true"
                      />
                    ) : (
                      <Plus
                        className="w-6 h-6 text-gray-700 shrink-0 ml-4"
                        aria-hidden="true"
                      />
                    )}
                  </button>

                  {expanded && (
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={btnId}
                      className="px-4 sm:px-6 pb-4 text-gray-700 text-sm sm:text-base leading-relaxed"
                    >
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <BlogSection title="Derniers articles" mode="related" relatedCount={3} />
    </>
  );
}
